// src/contexts/ConfirmDialogContext.tsx
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import ConfirmDialog, { ConfirmDialogType } from '../components/common/ConfirmDialog';

interface ConfirmDialogOptions {
  type?: ConfirmDialogType;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  cancelIsPrimary?: boolean;
  requiresInput?: boolean;
  inputPlaceholder?: string;
  inputLabel?: string;
}

interface ConfirmDialogContextValue {
  showConfirm: (options: ConfirmDialogOptions) => Promise<boolean>;
  confirm: (options: ConfirmDialogOptions) => Promise<string | null>;
  confirmDelete: (itemName: string, additionalMessage?: string) => Promise<boolean>;
  confirmAction: (title: string, message: string) => Promise<boolean>;
  confirmDestructive: (title: string, message: string) => Promise<boolean>;
}

const ConfirmDialogContext = createContext<ConfirmDialogContextValue | undefined>(undefined);

// 內部狀態類型
interface DialogState {
  isOpen: boolean;
  options: ConfirmDialogOptions;
  resolve: (value: boolean | string | null) => void;
  requiresInput: boolean;
}

// ConfirmDialog Provider 組件
export const ConfirmDialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [dialogState, setDialogState] = useState<DialogState | null>(null);

  // 核心 showConfirm 方法（返回 boolean）
  const showConfirm = useCallback((options: ConfirmDialogOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setDialogState({
        isOpen: true,
        options: {
          type: 'custom',
          ...options
        },
        resolve: resolve as (value: boolean | string | null) => void,
        requiresInput: false
      });
    });
  }, []);

  // 新方法：confirm（可返回輸入值）
  const confirm = useCallback((options: ConfirmDialogOptions): Promise<string | null> => {
    return new Promise((resolve) => {
      setDialogState({
        isOpen: true,
        options: {
          type: 'custom',
          ...options
        },
        resolve: resolve as (value: boolean | string | null) => void,
        requiresInput: options.requiresInput || false
      });
    });
  }, []);

  // 處理確認
  const handleConfirm = useCallback((inputValue?: string) => {
    if (dialogState) {
      if (dialogState.requiresInput) {
        dialogState.resolve(inputValue || null);
      } else {
        dialogState.resolve(true);
      }
      setDialogState(null);
    }
  }, [dialogState]);

  // 處理取消
  const handleCancel = useCallback(() => {
    if (dialogState) {
      if (dialogState.requiresInput) {
        dialogState.resolve(null);
      } else {
        dialogState.resolve(false);
      }
      setDialogState(null);
    }
  }, [dialogState]);

  // 便捷方法：刪除確認
  const confirmDelete = useCallback(async (
    itemName: string, 
    additionalMessage?: string
  ): Promise<boolean> => {
    const baseMessage = `Are you sure you want to delete "${itemName}"?`;
    const message = additionalMessage
      ? `${baseMessage}\n\n${additionalMessage}\n\nThis action cannot be undone.`
      : `${baseMessage}\n\nThis action cannot be undone.`;
    
    return showConfirm({
      type: 'delete',
      title: 'Delete Confirmation',
      message,
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
  }, [showConfirm]);

  // 便捷方法：一般確認
  const confirmAction = useCallback(async (
    title: string, 
    message: string
  ): Promise<boolean> => {
    return showConfirm({
      type: 'info',
      title,
      message,
      confirmText: 'Confirm',
      cancelText: 'Cancel'
    });
  }, [showConfirm]);

  // 便捷方法：危險操作確認
  const confirmDestructive = useCallback(async (
    title: string,
    message: string
  ): Promise<boolean> => {
    return showConfirm({
      type: 'warning',
      title,
      message,
      confirmText: 'Continue',
      cancelText: 'Cancel'
    });
  }, [showConfirm]);

  // Memoize context value 避免不必要的重新渲染
  const contextValue = useMemo<ConfirmDialogContextValue>(() => ({
    showConfirm,
    confirm,
    confirmDelete,
    confirmAction,
    confirmDestructive
  }), [showConfirm, confirm, confirmDelete, confirmAction, confirmDestructive]);

  return (
    <ConfirmDialogContext.Provider value={contextValue}>
      {children}

      {/* 渲染 ConfirmDialog */}
      {dialogState && (
        <ConfirmDialog
          isOpen={dialogState.isOpen}
          type={dialogState.options.type || 'custom'}
          title={dialogState.options.title}
          message={dialogState.options.message}
          confirmText={dialogState.options.confirmText}
          cancelText={dialogState.options.cancelText}
          cancelIsPrimary={dialogState.options.cancelIsPrimary}
          requiresInput={dialogState.requiresInput}
          inputPlaceholder={dialogState.options.inputPlaceholder}
          inputLabel={dialogState.options.inputLabel}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </ConfirmDialogContext.Provider>
  );
};

// Hook for using ConfirmDialog context
export const useConfirmDialog = (): ConfirmDialogContextValue => {
  const context = useContext(ConfirmDialogContext);
  if (!context) {
    throw new Error('useConfirmDialog must be used within a ConfirmDialogProvider');
  }
  return context;
};

export default ConfirmDialogContext;